/* eslint-disable @typescript-eslint/naming-convention */
export const COLORS = {
  BLACK: '#000000',
  BLUE: {
    50: '#EAEDFD',
    100: '#CAD3FD',
    200: '#A5B2FD',
    300: '#8497FE',
    400: '#3354FF',
    500: '#2842C8',
    600: '#2D3F9A',
    700: '#2A356F',
    800: '#282D48',
    900: '#25262D',
  },
  CATEGORY_COLOR: {
    aquaBlue: '#3FD2E4',
    black: '#000000',
    blue: '#4A70FF',
    blueViolet: '#7326D3',
    green: '#09AB6E',
    hotPink: '#FF48AF',
    lime: '#80CE61',
    mentis: '#80CE61',
    mint: '#0FB5AE',
    oceanBlue: '#4046CA',
    orange: '#F68511',
    pink: '#DE3D82',
    purple: '#8C2981',
    redOrange: '#ED5616',
    skyBlue: '#13A1F6',
    violet: '#57157E',
    white: '#FFFFFF',
    yellow: '#F5D633',
  },
  GRAY: {
    50: '#FAFAFB',
    75: '#F5F5F7',
    100: '#EDEEF1',
    200: '#E5E6E9',
    300: '#D4D5D8',
    400: '#C5C6C9',
    500: '#B0B1B4',
    600: '#7F8083',
    700: '#656669',
    800: '#4B4C4F',
    900: '#2F3033',
    1000: '#1A1B1D',
  },
  GREEN: {
    50: '#DEF2E6',
    100: '#B5E3C9',
    200: '#88D2A8',
    300: '#5FC48B',
    400: '#1FAD5C',
    500: '#1E8148',
    600: '#21693F',
    700: '#214F35',
    800: '#21362A',
    900: '#242926',
  },
  RED: {
    100: '#F3B9BC',
    200: '#EE9196',
    300: '#E8636B',
    400: '#E0242E',
    50: '#FAE6E7',
    500: '#AA2229',
    600: '#852329',
    700: '#652528',
    800: '#422426',
    900: '#2A2323',
  },
  TRANSPARENCY: {
    100: 'rgba(47, 48, 51, 0.1)',
    200: 'rgba(47, 48, 51, 0.2)',
    300: 'rgba(47, 48, 51, 0.3)',
    400: 'rgba(47, 48, 51, 0.4)',
    500: 'rgba(47, 48, 51, 0.5)',
    600: 'rgba(47, 48, 51, 0.6)',
    700: 'rgba(47, 48, 51, 0.7)',
    800: 'rgba(47, 48, 51, 0.8)',
    900: 'rgba(47, 48, 51, 0.9)',
  },

  WHITE: '#FFFFFF',

  YELLOW: {
    50: '#FBF7DF',
    100: '#FAECAE',
    200: '#F8E37C',
    300: '#F7D94A',
    400: '#F5CB00',
    500: '#B49609',
    600: '#8E7810',
    700: '#6C5D19',
    800: '#47401F',
    900: '#2B2922',
  },
} as const;

/** Doddies 브랜드 컬러 (코랄 레드) */
export const DODDIES_RED = '#FF5A5F';
export const COLOR_PRIMARY = DODDIES_RED;

/**
 * 의미 토큰. CSS variable 어댑터로 light/dark 모드를 동시에 표현한다.
 * 정의는 `theme-css-vars.tsx`의 :root와 html.dark selector를 참조.
 */
export const TOKEN = {
  bgApp: 'var(--color-bg-app)',
  bgSurface: 'var(--color-bg-surface)',
  bgMuted: 'var(--color-bg-muted)',
  textPrimary: 'var(--color-text-primary)',
  textSecondary: 'var(--color-text-secondary)',
  textMuted: 'var(--color-text-muted)',
  border: 'var(--color-border)',
  borderStrong: 'var(--color-border-strong)',
  primary: 'var(--color-primary)',
  primaryBg: 'var(--color-primary-bg)',
} as const;

// ? 25.07.31 콘솔 파운데이션에 정의한 값으로 재정의
export const FONT_WEIGHT = {
  EXTRA_LIGHT: 100,
  LIGHT: 200,
  REGULAR: 300,
  MEDIUM: 400,
  SEMI_BOLD: 500,
  BOLD: 600,
  EXTRA_BOLD: 700,
};

export const FONT_SIZE = {
  10: '0.625rem',
  11: '0.6875rem', // 50
  12: '0.75rem', // 75
  13: '0.8125rem',
  14: '0.875rem', // 100
  16: '1rem', // 200
  18: '1.125rem', // 300
  20: '1.25rem',
  24: '1.5rem', // 400
  28: '1.75rem',
  32: '2rem', // 500
  36: '2.25rem',
  40: '2.5rem',
  42: '2.625rem', // 600
  48: '3rem', // 700
  54: '3.375rem',
  64: '4rem', // 800
} as const;

export const HEIGHT = {
  XS: '24px',
  SM: '32px',
  MD: '40px',
  LG: '48px',
  XL: '64px',
} as const;

export const SPACING = {
  1: '2px',
  2: '4px',
  3: '8px',
  4: '12px',
  5: '16px',
  6: '20px',
  7: '24px',
  8: '32px',
  9: '40px',
  10: '48px',
  11: '64px',
  12: '80px',
} as const;

/**
 * 앱 프레임 최대 폭 — 화면마다 흩어져 있던 `480px` 하드코딩의 단일 출처.
 *
 * @description 원본이 모바일 앱 시안이라 중앙 정렬 프레임을 유지하되, 넓은 화면에서는 폭을 키워
 *  "폰 화면을 PC에 띄워놓은" 인상을 줄인다. 값이 여러 곳에 복제돼 있으면 한 곳만 고쳤을 때
 *  화면마다 폭이 달라지므로(실제로 8곳에 퍼져 있었다) 여기서만 정의한다.
 */
export const APP_FRAME = {
  /** 기본(모바일) */
  base: '480px',
  /** 태블릿 이상 */
  md: '600px',
  /**
   * 데스크톱 — 이 구간부터 캐러셀은 그리드로 바뀌어 가로 스크롤이 사라진다.
   *
   * @description 화면 전체를 채우지 않는다. 1440px 에서 끝까지 늘리면 카드가 과하게 넓어지고
   *  글줄이 길어져 오히려 읽기 나쁘다. 1120 은 클래스 카드 3열이 각 350px 안팎으로 떨어지는 폭이다.
   *  전역 네비게이션(GlobalNav)도 같은 상수를 쓰므로 헤더와 본문의 좌우 선이 함께 움직인다.
   */
  lg: '1120px',
} as const;

/**
 * 프레임 폭 반응형 CSS 조각. 페이지 래퍼에 그대로 끼워 쓴다.
 * @example css`width: 100%; margin: 0 auto; ${APP_FRAME_CSS}`
 */
export const APP_FRAME_CSS = `
  max-width: ${APP_FRAME.base};

  @media (min-width: 768px) {
    max-width: ${APP_FRAME.md};
  }

  @media (min-width: 1024px) {
    max-width: ${APP_FRAME.lg};
  }
`;

export const LAYOUT = {
  NAV_BAR_HEIGHT: '56px',
  FOOTER_HEIGHT: '40px',
  FOOTER_SPACING: '40px',
  DEPLOY_NOTIFICATION_HEIGHT: '42px',
  SIDE_BAR_WIDTH: {
    COLLAPSED: '20px',
    EXPANDED: '260px',
  },
};
